import { TypedDocumentNode, gql } from '@apollo/client';
import { Fragment } from 'react';
import styled from 'styled-components';

import { Horizontal } from '@sorare/core/src/atoms/layout/flex';
import { useNewLobbyFF } from '@sorare/core/src/hooks/useNewLobbyFF';
import { isType } from '@sorare/core/src/lib/gql';

import { ClickableAnyRewardConfig } from 'components/rewards/ClickableAnyRewardConfig';
import {
  NewAnyRewardConfig,
  Variant as NewVariant,
} from 'components/rewards/NewAnyRewardConfig';
import { Variant } from 'components/rewards/RewardOverview/types';

import { So5RankingRewards_so5Ranking } from './__generated__/index.graphql';

const Separator = styled.div`
  width: 1px;
  height: var(--double-unit);
  background-color: var(--c-nd-150);
`;

type Props = { so5Ranking: So5RankingRewards_so5Ranking; variant: Variant };

export const So5RankingRewards = ({ so5Ranking, variant }: Props) => {
  const useNewLobby = useNewLobbyFF();

  const { eligibleOrSo5Rewards, so5Lineup } = so5Ranking;

  const rewardConfigs = eligibleOrSo5Rewards.flatMap(so5RewardOrConfig =>
    isType(so5RewardOrConfig, 'So5RewardConfig')
      ? so5RewardOrConfig.rewardConfigs
      : so5RewardOrConfig.rewards.map(reward => reward.config)
  );

  if (useNewLobby) {
    return (
      <Horizontal gap={1.5}>
        {rewardConfigs.map((rewardConfig, i) => (
          <Fragment key={rewardConfig.id}>
            <NewAnyRewardConfig
              anyRewardConfig={rewardConfig}
              rarity={so5Lineup.so5Leaderboard?.mainRarityType || undefined}
              variant={NewVariant.TEXT}
            />
            {i < rewardConfigs.length - 1 && <Separator />}
          </Fragment>
        ))}
      </Horizontal>
    );
  }

  return rewardConfigs.map(rewardConfig => (
    <ClickableAnyRewardConfig
      key={rewardConfig.id}
      anyRewardConfig={rewardConfig}
      rarity={so5Lineup.so5Leaderboard?.mainRarityType || undefined}
      variant={variant}
      so5League={so5Lineup.so5Leaderboard?.so5League}
    />
  ));
};

So5RankingRewards.fragments = {
  so5Ranking: gql`
    fragment So5RankingRewards_so5Ranking on So5Ranking {
      id
      eligibleOrSo5Rewards {
        ... on So5RewardConfig {
          score
          ranks
          rankPct
          rewardConfigs {
            id
            ...ClickableAnyRewardConfig_anyRewardConfigInterface
          }
        }
        ... on So5Reward {
          slug
          rewards {
            id
            config {
              id
              ...ClickableAnyRewardConfig_anyRewardConfigInterface
            }
          }
        }
      }
      so5Lineup {
        id
        so5Leaderboard {
          slug
          mainRarityType
          so5League {
            slug
            ...ClickableAnyRewardConfig_so5League
          }
        }
      }
    }
    ${ClickableAnyRewardConfig.fragments.anyRewardConfigInterface}
    ${ClickableAnyRewardConfig.fragments.so5League}
  ` as TypedDocumentNode<So5RankingRewards_so5Ranking>,
};
